import { useCallback, useEffect, useRef, useState } from 'react';
import { LEAD_COLUMNS, OPEN_STATUSES } from './config';

const NO_ACCESS = { ok: false, can_claim: false, supervisor: false };

// Datos del módulo de Leads en vivo para el usuario actual. Se usa una sola vez (en el tablero):
// el botón de la cabecera muestra cuántos leads hay sin coger y la ventana recibe los datos.
//
// Tiempo real: Supabase solo avisa de los cambios en filas que el usuario puede ver. Cuando alguien
// coge un lead, los demás dejan de verlo y no reciben ese aviso; por eso, tras cada acción, la app
// emite además una señal "changed" (sin datos) por el canal de la organización y todos recargan.
export function useLeadsLive({ supabase, orgId, userId }) {
  const [access, setAccess] = useState(NO_ACCESS);
  const [leads, setLeads] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [accessRows, setAccessRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const channelRef = useRef(null);
  const reloadRef = useRef(null);

  const reload = useCallback(async () => {
    if (!orgId || !userId) return;
    const { data: acc, error: accErr } = await supabase.rpc('my_lead_access');
    const a = accErr || !acc ? NO_ACCESS : { ...NO_ACCESS, ...acc };
    setAccess(a);
    if (!a.ok) {
      setLeads([]); setProfiles([]); setAccessRows([]); setLoading(false);
      return;
    }
    const [leadsRes, profilesRes, accessRes] = await Promise.all([
      supabase.from('leads').select(LEAD_COLUMNS).eq('org_id', orgId).order('received_at', { ascending: false }).limit(1000),
      supabase.from('profiles').select('id, full_name, role').eq('org_id', orgId),
      supabase.from('lead_access').select('profile_id, enabled, can_claim').eq('org_id', orgId)
    ]);
    if (leadsRes.error) setError(leadsRes.error.message);
    else { setError(''); setLeads(leadsRes.data || []); }
    setProfiles(profilesRes.data || []);
    setAccessRows(accessRes.data || []);
    setLoading(false);
  }, [supabase, orgId, userId]);
  reloadRef.current = reload;

  useEffect(() => {
    if (!orgId || !userId) return undefined;
    reload();
    let timer = null;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { if (reloadRef.current) reloadRef.current(); }, 400);
    };
    // Aviso ligero de la base de datos (trigger trg_rt_signal → canal privado org:<id>:leads) en cada cambio de
    // leads o lead_access. Llega a todos los de la empresa, también a quien deja de ver un lead (al cogerlo otro).
    const channel = supabase.channel(`org:${orgId}:leads`, { config: { private: true } });
    channel.on('broadcast', { event: 'changed' }, refresh);
    let subscribedBefore = false;
    let cancelled = false;
    Promise.resolve(supabase.realtime.setAuth()).catch(() => {}).finally(() => {
      if (cancelled) return;
      channel.subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        if (subscribedBefore) refresh();
        subscribedBefore = true;
      });
    });
    channelRef.current = channel;
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', refresh);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      channelRef.current = null;
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', refresh);
    };
  }, [supabase, orgId, userId, reload]);

  // Tras una acción propia: recarga al momento (al resto de pantallas les avisa la base de datos).
  const afterChange = useCallback(() => {
    reload();
  }, [reload]);

  const unassignedCount = access.ok && access.can_claim
    ? leads.filter(l => !l.assigned_to && OPEN_STATUSES.includes(l.status)).length
    : 0;

  return { access, leads, profiles, accessRows, loading, error, reload, afterChange, unassignedCount };
}
